import { completeTaskWithReply } from "./runtime-reply.js";
import type { Task } from "./types.js";

type A2ASendServer = {
  sendTask(targetAgent: string, messageText: string): Promise<unknown>;
  discoverPeers?(): Promise<Record<string, { status: string; card?: { name?: string; description?: string } }>>;
};

type Logger = { info: (...args: unknown[]) => void; error: (...args: unknown[]) => void };

type RuntimeResponder = (text: string, marker: string) => Promise<string>;

type AgentDirectory = {
  names: string[];
  aliases: Map<string, string>;
};

const FALLBACK_AGENT_ALIASES: Array<[string, string[]]> = [
  ["Perkos-Claw-Tester", ["perkos-claw-tester", "openclaw", "open claw", "claw"]],
  ["Perkos-Hermes-Tester", ["perkos-hermes-tester", "hermes"]],
  ["Morpheus-A2A-Receiver", ["morpheus-a2a-receiver", "morpheus"]],
  ["Apollo", ["apollo"]],
];

function includesAny(text: string, terms: string[]): boolean {
  const lower = text.toLowerCase();
  return terms.some((term) => lower.includes(term.toLowerCase()));
}

function requestedMarker(text: string, fallback: string): string {
  const patterns = [
    /termina(?:r)?(?:\s+exactamente)?\s+con\s+([A-Z0-9_]{6,})/i,
    /end(?:s)?(?:\s+exactly)?\s+with\s+([A-Z0-9_]{6,})/i,
    /marker\s+([A-Z0-9_]{6,})/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].toUpperCase();
  }
  return fallback;
}

function stripDelegationNoise(text: string): string {
  return text
    .replace(/^Delegated by .*?\.\s*/i, "")
    .replace(/^Delegated request:\s*/i, "")
    .replace(/^A2A-DELEGATION[^\n]*\n?/gim, "")
    .replace(/End with DELEGATED_A2A_DONE\.?/gi, "")
    .trim();
}

function normalizeKey(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function compactKey(value: string): string {
  return normalizeKey(value).replace(/\s+/g, "");
}

function addAlias(aliases: Map<string, string>, alias: string, canonical: string): void {
  const normalized = normalizeKey(alias);
  if (normalized) aliases.set(normalized, canonical);
  const compact = compactKey(alias);
  if (compact) aliases.set(compact, canonical);
}

function aliasesForName(name: string): string[] {
  const base = normalizeKey(name);
  const compact = compactKey(name);
  const withoutTester = base.replace(/\btester\b/g, "").replace(/\s+/g, " ").trim();
  const parts = base.split(" ").filter(Boolean);
  return Array.from(new Set([
    name,
    base,
    compact,
    withoutTester,
    parts.at(-1) || "",
    parts.at(0) || "",
  ].filter(Boolean)));
}

async function buildAgentDirectory(server: A2ASendServer, selfName: string): Promise<AgentDirectory> {
  const names = new Set<string>();
  const aliases = new Map<string, string>();
  names.add(selfName);
  for (const [name, extraAliases] of FALLBACK_AGENT_ALIASES) {
    names.add(name);
    for (const alias of [name, ...extraAliases]) addAlias(aliases, alias, name);
  }

  if (server.discoverPeers) {
    try {
      const peers = await server.discoverPeers();
      for (const [name, entry] of Object.entries(peers)) {
        const canonical = entry.card?.name || name;
        if (!canonical || canonical === selfName) continue;
        names.add(canonical);
        for (const alias of aliasesForName(canonical)) addAlias(aliases, alias, canonical);
        if (name !== canonical) for (const alias of aliasesForName(name)) addAlias(aliases, alias, canonical);
      }
    } catch {
      // Discovery is opportunistic; relay may be temporarily unavailable.
    }
  }

  for (const name of names) for (const alias of aliasesForName(name)) addAlias(aliases, alias, name);
  return { names: Array.from(names), aliases };
}

function resolveAgentName(raw: string, directory: AgentDirectory, selfName: string, allowSelf = false): string | undefined {
  const cleaned = raw.replace(/^@/, "").trim();
  if (!cleaned) return undefined;
  const normalized = normalizeKey(cleaned);
  const compact = compactKey(cleaned);

  for (const name of directory.names) {
    if (!allowSelf && name === selfName) continue;
    if (normalizeKey(name) === normalized || compactKey(name) === compact) return name;
  }

  const exact = directory.aliases.get(normalized) || directory.aliases.get(compact);
  if (exact && (allowSelf || exact !== selfName)) return exact;

  const hits: Array<{ canonical: string; index: number; aliasLength: number }> = [];
  const entries = Array.from(directory.aliases.entries()).sort((a, b) => b[0].length - a[0].length);
  for (const [alias, canonical] of entries) {
    if (!allowSelf && canonical === selfName) continue;
    if (alias.length < 4) continue;
    const normalizedIndex = normalized.indexOf(alias);
    const compactIndex = compact.indexOf(alias.replace(/\s+/g, ""));
    const index = normalizedIndex >= 0 ? normalizedIndex : compactIndex;
    if (index >= 0) hits.push({ canonical, index, aliasLength: alias.length });
  }
  hits.sort((a, b) => a.index - b.index || b.aliasLength - a.aliasLength);
  if (hits[0]) return hits[0].canonical;
  return undefined;
}

function findMentionedAgents(text: string, directory: AgentDirectory, selfName: string): string[] {
  const normalized = normalizeKey(stripDelegationNoise(text));
  const compact = compactKey(stripDelegationNoise(text));
  const hits: Array<{ name: string; index: number }> = [];
  const seen = new Set<string>();
  const entries = Array.from(directory.aliases.entries()).sort((a, b) => b[0].length - a[0].length);
  for (const [alias, canonical] of entries) {
    if (canonical === selfName || alias.length < 3 || seen.has(canonical)) continue;
    const idx = normalized.indexOf(alias);
    const compactIdx = compact.indexOf(alias.replace(/\s+/g, ""));
    const index = idx >= 0 ? idx : compactIdx;
    if (index >= 0) {
      hits.push({ name: canonical, index });
      seen.add(canonical);
    }
  }
  return hits.sort((a, b) => a.index - b.index).map((hit) => hit.name);
}

function parseArrowRoute(text: string, directory: AgentDirectory, selfName: string): string[] {
  const clean = stripDelegationNoise(text);
  const routeLine = clean.split(/\n/).find((line) => /(?:->|→|=>|➡)/.test(line));
  if (!routeLine) return [];
  const route = routeLine
    .split(/(?:->|→|=>|➡)/)
    .map((part) => resolveAgentName(part, directory, selfName) || (normalizeKey(part).includes(normalizeKey(selfName)) ? selfName : undefined))
    .filter(Boolean) as string[];
  return Array.from(new Set(route));
}

function detectTarget(text: string, selfName: string, directory: AgentDirectory): string | undefined {
  const route = parseDelegationEnvelope(text).route;
  if (route.length) return nextRouteTarget(route, selfName);
  const arrowRoute = parseArrowRoute(text, directory, selfName);
  if (arrowRoute.length) return nextRouteTarget(arrowRoute, selfName) || arrowRoute.find((name) => name !== selfName);
  return findMentionedAgents(text, directory, selfName)[0];
}

function detectReplyTarget(text: string, selfName: string, directory: AgentDirectory): string | undefined {
  const envelope = parseDelegationEnvelope(text);
  if (envelope.notify && envelope.notify !== selfName) return envelope.notify;

  const clean = stripDelegationNoise(text);
  const patterns = [
    /(?:reply|respond|notify|send|return|env[ií]a|enviar|responde|notifica)(?:\s+the\s+result)?\s+(?:to|a)\s+([A-Za-z0-9_. -]{2,80}?)(?:[.;,\n]|$)/i,
    /(?:notify|notifica|reply|responde)\s+([A-Za-z0-9_. -]{2,80}?)(?:[.;,\n]|$)/i,
    /(?:final|ultimo|último).*?(?:notify|notifica|responde|reply).*?(?:to|a)?\s+([A-Za-z0-9_. -]{2,80}?)(?:[.;,\n]|$)/i,
  ];
  for (const pattern of patterns) {
    const match = clean.match(pattern);
    const resolved = match?.[1] ? resolveAgentName(match[1], directory, selfName, true) : undefined;
    if (resolved) return resolved;
  }

  if (includesAny(clean, ["responda a", "envíe", "envia", "send", "reply", "notify", "notifica", "morpheus"])) {
    const mentioned = findMentionedAgents(clean, directory, selfName);
    return mentioned.at(-1);
  }
  return undefined;
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url, {
    headers: { accept: "application/json", "user-agent": "perkos-a2a-research/0.8" },
    signal: AbortSignal.timeout(10_000),
  });
  if (!response.ok) throw new Error(`${url} returned ${response.status}`);
  return response.json();
}

async function researchEthPrice(): Promise<string> {
  const sources: string[] = [];
  try {
    const cg = await fetchJson("https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd&include_24hr_change=true&include_last_updated_at=true") as any;
    const eth = cg?.ethereum;
    if (eth?.usd) {
      const updated = eth.last_updated_at ? new Date(Number(eth.last_updated_at) * 1000).toISOString() : "unknown";
      const change = typeof eth.usd_24h_change === "number" ? ` (${eth.usd_24h_change.toFixed(2)}% 24h)` : "";
      sources.push(`CoinGecko ETH/USD: $${Number(eth.usd).toLocaleString("en-US")}${change}, updated ${updated}`);
    }
  } catch (err) {
    sources.push(`CoinGecko unavailable: ${err instanceof Error ? err.message : String(err)}`);
  }

  try {
    const cb = await fetchJson("https://api.coinbase.com/v2/prices/ETH-USD/spot") as any;
    const amount = cb?.data?.amount;
    if (amount) sources.push(`Coinbase ETH/USD spot: $${Number(amount).toLocaleString("en-US")}`);
  } catch (err) {
    sources.push(`Coinbase unavailable: ${err instanceof Error ? err.message : String(err)}`);
  }

  if (!sources.some((s) => s.includes("ETH/USD"))) {
    return [
      "No pude confirmar el precio vivo de ETH desde las APIs públicas en este momento.",
      "Metodología recomendada: consultar CoinGecko `/simple/price`, Coinbase `/v2/prices/ETH-USD/spot`, Binance/Kraken ticker y comparar timestamps.",
    ].join("\n");
  }

  return [
    "Resultado de research de precio ETH:",
    ...sources.map((source) => `- ${source}`),
    "Nota: validar con más de una fuente antes de usarlo para trading/decisiones financieras.",
  ].join("\n");
}

function extractTaskFinalText(result: unknown): string {
  const anyResult = result as any;
  const task = anyResult.result?.result || anyResult.result || anyResult.payload?.result || anyResult;
  const snippets: string[] = [];
  const statusParts = task?.status?.message?.parts || [];
  for (const part of statusParts) if (typeof part?.text === "string") snippets.push(part.text);
  for (const artifact of task?.artifacts || []) {
    for (const part of artifact?.parts || []) if (typeof part?.text === "string") snippets.push(part.text);
  }
  return snippets.at(-1) || JSON.stringify(result);
}

function hasA2ARelayExplainTask(text: string): boolean {
  return includesAny(text, ["a2a relay", "relay a2a", "qué es un a2a relay", "what an a2a relay is"]);
}

function answerA2ARelay(selfName: string): string {
  return [
    `${selfName} result:`,
    "An A2A relay is a transport/router that lets agents exchange tasks and responses by agent name even when they are behind NAT, on different runtimes, or on the same host with different ports.",
    "It handles registration, discovery, message routing, heartbeats, and delivery fallback while each agent keeps its own runtime and local permissions.",
  ].join("\n");
}

function parseDelegationEnvelope(text: string): { route: string[]; notify?: string; marker?: string; depth: number } {
  const line = text.match(/^A2A-DELEGATION[^\n]*/im)?.[0] || "";
  const routeRaw = line.match(/\broute=([^\s]+)/i)?.[1];
  const notifyRaw = line.match(/\bnotify=([^\s]+)/i)?.[1];
  const markerRaw = line.match(/\bmarker=([A-Z0-9_]{6,})/i)?.[1];
  const depthRaw = line.match(/\bdepth=(\d+)/i)?.[1];
  return {
    route: routeRaw ? routeRaw.split(/[|,]/).map((x) => x.trim()).filter(Boolean) : [],
    notify: notifyRaw && notifyRaw !== "none" ? notifyRaw : undefined,
    marker: markerRaw,
    depth: depthRaw ? Number(depthRaw) : 0,
  };
}

function delegationDepth(text: string): number {
  return parseDelegationEnvelope(text).depth;
}

function nextRouteTarget(route: string[], selfName: string): string | undefined {
  const selfIndex = route.findIndex((name) => name === selfName);
  if (selfIndex >= 0) return route.slice(selfIndex + 1).find((name) => name !== selfName);
  return route.find((name) => name !== selfName);
}

function cleanNestedResult(text: string, finalMarker: string): string {
  const escaped = finalMarker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text
    .replace(new RegExp(`\n?${escaped}\s*$`, "g"), "")
    .replace(/\n?DELEGATED_A2A_DONE\s*$/g, "")
    .trim();
}

function routeForDelegation(selfName: string, target: string, text: string, directory: AgentDirectory): string[] {
  const envelopeRoute = parseDelegationEnvelope(text).route;
  if (envelopeRoute.length) return Array.from(new Set(envelopeRoute));
  const arrowRoute = parseArrowRoute(text, directory, selfName);
  if (arrowRoute.length) return Array.from(new Set(arrowRoute.includes(selfName) ? arrowRoute : [selfName, ...arrowRoute]));
  const mentions = findMentionedAgents(text, directory, selfName).filter((name) => name !== selfName);
  const route = [selfName, target, ...mentions.filter((name) => name !== target)];
  return Array.from(new Set(route));
}

function buildDelegatedMessage(input: {
  originalText: string;
  selfName: string;
  target: string;
  route: string[];
  marker: string;
  notify?: string;
}): string {
  const { originalText, selfName, target, route, marker, notify } = input;
  const depth = delegationDepth(originalText) + 1;
  const clean = stripDelegationNoise(originalText);
  const header = `A2A-DELEGATION origin=${selfName} target=${target} route=${route.join("|")} notify=${notify || "none"} depth=${depth} marker=${marker}`;

  if (hasA2ARelayExplainTask(clean) && !nextRouteTarget(route, target)) {
    return [
      header,
      "Complete this task directly. Briefly explain what an A2A relay is and why agents use it.",
      "Do not delegate this request further unless explicitly required by a new instruction.",
      "End with DELEGATED_A2A_DONE.",
    ].join("\n");
  }

  return [
    header,
    "Complete or delegate the following task once. Follow the A2A-DELEGATION route exactly. Do not echo this request back to agents already named in the route.",
    clean,
    notify ? `When the route is complete, send or return the final useful result to ${notify}.` : "Return the final useful result to the caller.",
    `End exactly with ${marker}.`,
  ].join("\n");
}

async function completeTerminalTask(input: {
  selfName: string;
  server: A2ASendServer;
  task: Task;
  text: string;
  marker: string;
  replyTarget?: string;
  runtimeResponder?: RuntimeResponder;
}): Promise<void> {
  const { selfName, server, task, text, marker, replyTarget, runtimeResponder } = input;
  const cleanText = stripDelegationNoise(text);
  const wantsEthResearch = includesAny(cleanText, ["precio de eth", "eth price", "ethereum price", "precio actual de eth"]);

  let finalText: string;
  if (includesAny(cleanText, ["A2A_COMM_TEST", "communication test", "ping test", "confirm receipt", "confirm receive", "confirmar recibo", "confirma recibo"])) {
    finalText = [`${selfName} received and completed the A2A communication test.`, marker].join("\n");
  } else if (hasA2ARelayExplainTask(cleanText)) {
    finalText = [answerA2ARelay(selfName), "", marker].join("\n");
  } else if (wantsEthResearch) {
    finalText = [await researchEthPrice(), "", marker].join("\n");
  } else if (runtimeResponder) {
    const reply = await runtimeResponder(cleanText, marker);
    finalText = [reply, "", marker].join("\n");
  } else {
    finalText = [`${selfName} completed the delegated A2A task.`, cleanText, marker].join("\n");
  }

  if (replyTarget && replyTarget !== selfName) {
    const replyResult = await server.sendTask(replyTarget, [
      `Final A2A result from ${selfName}:`,
      "",
      cleanNestedResult(finalText, marker),
      "",
      "A2A_FINAL_NOTIFICATION_OK",
    ].join("\n"));
    finalText = [
      cleanNestedResult(finalText, marker),
      "",
      `Forwarded final result to ${replyTarget}.`,
      extractTaskFinalText(replyResult),
      "",
      marker,
    ].join("\n");
  }

  completeTaskWithReply(task, finalText);
}

export async function tryHandleAgenticTask(input: {
  selfName: string;
  server: A2ASendServer;
  task: Task;
  text: string;
  logger: Logger;
  runtimeResponder?: RuntimeResponder;
}): Promise<boolean> {
  const { selfName, server, task, text, logger, runtimeResponder } = input;
  const directory = await buildAgentDirectory(server, selfName);
  const envelope = parseDelegationEnvelope(text);
  const cleanText = stripDelegationNoise(text);
  const wantsDelegation = includesAny(cleanText, ["usa comunicación a2a", "usar comunicación a2a", "pídele", "pidele", "dile a", "delegate", "delegar", "tell", "ask", "route", "chain", "handoff", "notify", "notifica"]);
  const wantsEthResearch = includesAny(cleanText, ["precio de eth", "eth price", "ethereum price", "precio actual de eth"]);
  const finalMarker = envelope.marker || requestedMarker(text, envelope.route.length ? "DELEGATED_A2A_DONE" : "TRI_HERMES_DONE");

  if (/^(Final A2A result|Delegated result from|Research result from)\b/i.test(cleanText) && !envelope.route.length) {
    completeTaskWithReply(task, [`${selfName} received the final A2A notification.`, "", cleanText, "", requestedMarker(text, "A2A_NOTIFICATION_RECEIVED")].join("\n"));
    return true;
  }

  if (includesAny(cleanText, ["A2A_COMM_TEST", "communication test", "ping test", "confirm receipt", "confirm receive", "confirmar recibo", "confirma recibo"]) && !wantsDelegation && !envelope.route.length) {
    completeTaskWithReply(task, [
      `${selfName} received and completed the A2A communication test.`,
      finalMarker,
    ].join("\n"));
    return true;
  }

  if (hasA2ARelayExplainTask(cleanText) && !wantsDelegation && !envelope.route.length) {
    completeTaskWithReply(task, [answerA2ARelay(selfName), "", finalMarker].join("\n"));
    return true;
  }

  if (wantsEthResearch && !wantsDelegation && !envelope.route.length) {
    const research = await researchEthPrice();
    const replyTarget = detectReplyTarget(text, selfName, directory);
    let replyResultText = "";
    if (replyTarget) {
      const replyResult = await server.sendTask(replyTarget, [
        `Research result from ${selfName}:`,
        "",
        research,
        "",
        "DELEGATED_ETH_RESEARCH_DONE",
      ].join("\n"));
      replyResultText = extractTaskFinalText(replyResult);
    }
    completeTaskWithReply(task, [
      research,
      "",
      replyTarget ? `Forwarded to ${replyTarget}.` : undefined,
      replyResultText ? `Reply delivery result:\n${replyResultText}` : undefined,
      finalMarker,
    ].filter(Boolean).join("\n"));
    return true;
  }

  if (!wantsDelegation && !envelope.route.length) return false;

  if (delegationDepth(text) >= 8) {
    completeTaskWithReply(task, [
      `A2A delegation stopped by ${selfName}: max delegation depth reached.`,
      "This prevents routing loops across heterogeneous agents.",
      requestedMarker(text, "A2A_DELEGATION_STOPPED"),
    ].join("\n"));
    return true;
  }

  const target = detectTarget(text, selfName, directory);
  const replyTarget = detectReplyTarget(text, selfName, directory);
  if (!target) {
    await completeTerminalTask({ selfName, server, task, text, marker: finalMarker, replyTarget, runtimeResponder });
    return true;
  }

  const route = routeForDelegation(selfName, target, text, directory);
  const next = nextRouteTarget(route, selfName) || target;
  if (!next || next === selfName) {
    await completeTerminalTask({ selfName, server, task, text, marker: finalMarker, replyTarget, runtimeResponder });
    return true;
  }

  const delegatedMessage = wantsEthResearch
    ? [
        `A2A-DELEGATION origin=${selfName} target=${next} route=${route.join("|")} notify=${replyTarget || "none"} depth=${delegationDepth(text) + 1} marker=${finalMarker}`,
        "Delegated research request. Research the current ETH price using available live/public APIs.",
        "Return concise findings with source names, timestamp if available, and limitations.",
        replyTarget ? `If possible, send the final result to ${replyTarget} via A2A.` : "Return the final useful result to the caller.",
        "End with DELEGATED_ETH_RESEARCH_DONE.",
      ].filter(Boolean).join("\n")
    : buildDelegatedMessage({ originalText: text, selfName, target: next, route, marker: finalMarker, notify: replyTarget });

  logger.info(`[perkos-a2a] Agentic delegation: ${selfName} -> ${next}${replyTarget ? ` -> notify ${replyTarget}` : ""}; route=${route.join(" -> ")}`);
  const delegatedResult = await server.sendTask(next, delegatedMessage);
  const delegatedText = cleanNestedResult(extractTaskFinalText(delegatedResult), finalMarker);

  let replyResultText = "";
  if (replyTarget && replyTarget !== next && !delegatedText.includes("Forwarded final result to")) {
    const replyMessage = [
      `Delegated result from ${next}, forwarded by ${selfName}:`,
      "",
      delegatedText,
      "",
      "End-to-end A2A chain completed. TRI_AGENTIC_RESULT_DELIVERED.",
    ].join("\n");
    const replyResult = await server.sendTask(replyTarget, replyMessage);
    replyResultText = extractTaskFinalText(replyResult);
  }

  completeTaskWithReply(task, [
    `Agentic A2A delegation completed by ${selfName}.`,
    `Delegated target: ${next}`,
    `Route: ${route.join(" -> ")}`,
    replyTarget ? `Reply target: ${replyTarget}` : undefined,
    "",
    "Delegated result:",
    delegatedText,
    replyResultText ? "" : undefined,
    replyResultText ? "Reply delivery result:" : undefined,
    replyResultText || undefined,
    "",
    finalMarker,
  ].filter(Boolean).join("\n"));
  return true;
}
